import React, {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {ListService} from '../Types/service';
import Button from 'react-bootstrap/Button';
import IconeService from './IconeService';
import {useIntl} from 'react-intl';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {isClientCIS} from '../../Auth/Utils/CustomerHelper';
import {getG2000Link} from '../../../app/pages/dashboard/api';
import {useClientLogin} from '../../../app/hooks/useClientLogin';
import {
  accessorService,
  accessorSite,
  getBuColLabel,
  getServiceColLabel,
  getSiteColLabel,
  getTelephonyDetailsPath,
  shouldOpenInternalTelephonyDetails,
} from '../Helpers/ServiceHelper';
import {useNavigate} from 'react-router-dom';
import styles from './Services.module.scss';

interface AccountOption {
  value: string;
  buId: number;
  buName: string;
  codeX3: string;
  count: number;
  label: string;
}

interface SiteOption {
  value: number;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const CheckboxOption = <T extends {label: string; count?: number}>(props: OptionProps<T, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="me-2 mt-1 flex-shrink-0"
      />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
      {props.data.count != null && (
        <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
      )}
    </div>
  </components.Option>
);

interface TelephonieProps {
  isLoading: boolean;
  services?: ListService[];
}

const Telephonie = ({isLoading, services}: TelephonieProps): JSX.Element => {
  const customer = useGetMe();
  const userLogin = useClientLogin();
  const navigate = useNavigate();

  const intl = useIntl();

  const [selectedAccounts, setSelectedAccounts] = useState<AccountOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<SiteOption[]>([]);

  const accountOptions = useMemo<AccountOption[]>(() => {
    if (!services) return [];
    const map = new Map<string, AccountOption>();
    services.forEach((s) => {
      if (s.buId == null) return;
      const buName = s.buName ?? '';
      const codeX3 = s.codeX3 ?? '';
      const key = `${s.buId}|${codeX3}|${buName}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = [s.buId, codeX3, buName].filter((p) => p !== '' && p != null).join(' - ');
      map.set(key, {value: key, buId: s.buId, buName, codeX3, label, count: 1});
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        a.buId - b.buId || a.codeX3.localeCompare(b.codeX3) || a.buName.localeCompare(b.buName)
    );
  }, [services]);

  const siteOptions = useMemo<SiteOption[]>(() => {
    if (!services) return [];
    const map = new Map<number, SiteOption>();
    services.forEach((s) => {
      const site = s.site;
      if (site?.id == null) return;
      const existing = map.get(site.id);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = `${site.id} - ${[site.address, site.zipCode, site.city]
        .filter(Boolean)
        .join(' ')}`.trim();
      map.set(site.id, {value: site.id, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.value - b.value);
  }, [services]);

  const filteredServices = useMemo(() => {
    if (!services) return services;
    if (selectedAccounts.length === 0 && selectedSites.length === 0) return services;
    const accountKeys = new Set(selectedAccounts.map((o) => o.value));
    const siteIds = new Set(selectedSites.map((o) => o.value));
    return services.filter((s) => {
      const accountOk =
        accountKeys.size === 0 ||
        (s.buId != null && accountKeys.has(`${s.buId}|${s.codeX3 ?? ''}|${s.buName ?? ''}`));
      const siteOk = siteIds.size === 0 || (s.site?.id != null && siteIds.has(s.site.id));
      return accountOk && siteOk;
    });
  }, [services, selectedAccounts, selectedSites]);

  const renderPhoneAction = useCallback(
    (row: ListService): JSX.Element => {
      if (!isClientCIS(customer)) {
        return (
          <IconeService
            lien="https://extranet2.stella-telecom.fr/telephonie-fixe"
            ariaLabel={intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE'})}
            iconClass="fs-1 fa-solid fa-phone"
            title={intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE'})}
            targetLink="_blank"
          />
        );
      }

      const site = row.site;

      return (
        <div>
          <IconeService
            ariaLabel={intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE'})}
            iconClass="fs-1 fa-solid fa-phone"
            title={intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE'})}
            onClick={(e) => {
              e.preventDefault();
              const openDetails = () =>
                navigate(getTelephonyDetailsPath(row));
              if (shouldOpenInternalTelephonyDetails(row) || !site) {
                openDetails();
                return;
              }
              getG2000Link(site.id.toString(), userLogin)
                .then((link) => {
                  window.open(link, '_blank');
                })
                .catch(openDetails);
            }}
          />
          {site && (
            <span
              style={{
                color: '#f8fafb',
                position: 'relative',
                left: '-21px',
                top: '-10px',
                fontSize: '11px',
                fontWeight: 'bold',
              }}
            >
              +
            </span>
          )}
        </div>
      );
    },
    [customer, intl, navigate, userLogin]
  );

  const columns = useMemo<ColumnDef<ListService>[]>(() => {
    if (isClientCIS(customer)) {
      return [
        {
          header: intl.formatMessage({id: 'SERVICES.COL.ACCOUNT'}),
          accessorKey: 'buId',
          cell: getBuColLabel,
          size: 200,
        },
        {
          header: intl.formatMessage({id: 'SERVICES.COL.SITE'}),
          accessorKey: 'siteId',
          cell: getSiteColLabel,
          accessorFn: accessorSite,
          size: 250,
          sortingFn: (rowA, rowB) => {
            const idA = rowA.original.site?.id ?? 0;
            const idB = rowB.original.site?.id ?? 0;
            return idA - idB;
          },
        },
        {
          header: intl.formatMessage({id: 'TELEPHONIE.LISTE.COL_NUMEROS'}),
          accessorKey: 'numeros',
          cell: ({row}) => row.original.phoneNumber || null,
          size: 150,
        },
        {
          header: intl.formatMessage({id: 'SERVICES.COL.SERVICE'}),
          accessorKey: 'service',
          cell: getServiceColLabel,
          accessorFn: accessorService,

          sortingFn: (rowA, rowB) => {
            const idA = rowA.original.serviceId ?? 0;
            const idB = rowB.original.serviceId ?? 0;
            return idA - idB;
          },
        },
        {
          header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACTIONS'}),
          accessorKey: 'telephone',
          size: 60,
          cell: (info) => renderPhoneAction(info.row.original),
        },
      ];
    } else {
      return [
        {
          header: intl.formatMessage({id: 'SERVICES.COL.ACCOUNT'}),
          accessorKey: 'buId',
          size: 200,
          cell: (info) => {
            let title = '-';

            if (info.row.original.buId) {
              title = info.row.original.buId.toString();
            }

            if (info.row.original.buName) {
              title += ` - ${info.row.original.buName}`;
            }

            return title;
          },
        },
        {
          header: intl.formatMessage({id: 'SERVICES.COL.SITE'}),
          accessorKey: 'siteId',
          accessorFn: accessorSite,
          size: 250,
          cell: (info) => {
            let title = '-';

            const site = info.row.original.site;

            if (site) {
              title = `${site.id} - ${site.address} ${site.zipCode} ${site.city}`;
            }

            return title;
          },
        },
        {
          header: intl.formatMessage({id: 'TELEPHONIE.LISTE.COL_NUMEROS'}),
          accessorKey: 'numeros',
          cell: ({row}) => row.original.phoneNumber || null,
          size: 150,
        },
        {
          header: intl.formatMessage({id: 'SERVICES.COL.SERVICE'}),
          accessorKey: 'service',
          accessorFn: accessorService,
          size: 220,
          cell: (info) => {
            let title = '-';

            if (info.row.original.serviceId) {
              title = `${info.row.original.serviceId}`;
            }

            if (info.row.original.serviceId) {
              title += ` - ${info.row.original.serviceName}`;
            }

            return title;
          },
        },
        {
          header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACTIONS'}),
          accessorKey: 'telephone',
          size: 60,
          cell: (info) => renderPhoneAction(info.row.original),
        },
      ];
    }
  }, [customer, intl, renderPhoneAction]);

  const renderMobileCard = (row: ListService): JSX.Element => {
    const cellInfo = {row: {original: row}};

    return (
      <div className={styles.mobileCardBody}>
        <div className={styles.mobileCardHeader}>
          {getServiceColLabel(cellInfo)}
          {renderPhoneAction(row)}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>Compte</span>
          {getBuColLabel(cellInfo)}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>Site</span>
          {getSiteColLabel(cellInfo)}
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>Numéros</span>
          <span>{row.phoneNumber || '-'}</span>
        </div>
      </div>
    );
  };

  const filtersToolbar = (
    <>
      <div className={styles.filterSelectWrapper}>
        <ReactSelect<AccountOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className={styles.filterSelectWrapperWide}>
        <ReactSelect<SiteOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={siteOptions}
          value={selectedSites}
          onChange={(vals) => setSelectedSites(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SITES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
    </>
  );

  return (
    <>
      <div className="d-flex align-items-center justify-content-end gap-3 mb-3">
        <p className="mb-0">{intl.formatMessage({id: 'TELEPHONIE.LISTE.SUBTITLE'})}</p>
        <Button
          className="btn btn-primary btn-sm"
          href={
            !isClientCIS(customer)
              ? 'https://cataloguemobile.stellatelecom.com/'
              : 'https://commande.celeste.fr/mobile'
          }
          target="_blank"
        >
          {intl.formatMessage({id: 'COMMON.ORDER_NOW'})}
        </Button>
      </div>
      <TableMetronic<ListService>
        toolbar={filtersToolbar}
        stackedHeader
        title={intl.formatMessage({id: 'TELEPHONIE.LISTE.TITLE'})}
        data={filteredServices ?? []}
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={isLoading}
        defaultSorting={[
          {
            id: 'siteId',
            desc: true,
          },
        ]}
        searchBar
        pagination
      />
    </>
  );
};

export default Telephonie;
